import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SubscriptionPayment from './components/SubscriptionPayment';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

const plans = [
  {
    name: 'Starter',
    slug: 'starter',
    badge: 'Free Forever',
    price: 0,
    highlight: 'For new & first-time event hosts',
    commission: '10%',
    commissionLabel: 'commission per sale',
    accentFrom: 'from-teal-400',
    accentTo: 'to-cyan-400',
    ringColor: 'ring-teal-400',
    badgeColor: 'bg-teal-50 text-teal-700',
    popular: false,
    features: [
      { text: 'Up to 3 events per month', included: true },
      { text: '10% platform commission', included: true },
      { text: 'Social follow gate (2 accounts)', included: true },
      { text: 'Online store (up to 10 products)', included: true },
      { text: 'Basic analytics', included: true },
      { text: 'Email support', included: true },
      { text: 'Advanced analytics & campaigns', included: false },
      { text: 'Team access', included: false },
    ],
  },
  {
    name: 'Growth',
    slug: 'growth',
    badge: 'Most Popular',
    price: 19,
    highlight: 'For growing event organizers',
    commission: '5%',
    commissionLabel: 'commission per sale',
    accentFrom: 'from-teal-500',
    accentTo: 'to-teal-600',
    ringColor: 'ring-teal-500',
    badgeColor: 'bg-teal-600 text-white',
    popular: true,
    features: [
      { text: 'Unlimited events', included: true },
      { text: '5% platform commission', included: true },
      { text: 'Social follow gate (5 accounts)', included: true },
      { text: 'Unlimited store products', included: true },
      { text: 'Advanced analytics + email campaigns', included: true },
      { text: 'Scheduled email reminders', included: true },
      { text: 'Team access (up to 3 members)', included: true },
      { text: 'Priority support', included: true },
    ],
  },
  {
    name: 'Pro Partner',
    slug: 'pro_partner',
    badge: 'Power Users',
    price: 39,
    highlight: 'Everything you need to scale',
    commission: '2%',
    commissionLabel: 'commission per sale',
    accentFrom: 'from-gray-800',
    accentTo: 'to-gray-900',
    ringColor: 'ring-gray-800',
    badgeColor: 'bg-gray-900 text-white',
    popular: false,
    features: [
      { text: 'Everything in Growth', included: true },
      { text: '2% platform commission', included: true },
      { text: 'Homepage spotlight & featured placement', included: true },
      { text: 'Unlimited social follow accounts', included: true },
      { text: 'Custom event branding', included: true },
      { text: 'Unlimited team members', included: true },
      { text: 'Dedicated account support', included: true },
      { text: 'Early access to new features', included: true },
    ],
  },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

export default function SubscribePage() {
  const { user, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<string | null>('Growth');
  const [step, setStep] = useState<'select' | 'payment'>('select');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successRef, setSuccessRef] = useState('');
  const [existingSub, setExistingSub] = useState<any>(null);
  const [loadingSub, setLoadingSub] = useState(true);

  useEffect(() => {
    const PAGE_TITLE = 'Pricing Plans – Start Creating Events | EVENTSHOWCASE';
    const PAGE_DESC = 'Choose a plan on EVENTSHOWCASE. Start free or upgrade to Growth and Pro Partner plans to unlock unlimited events, lower fees, and advanced analytics.';
    const PAGE_URL = `${SITE_URL}/create`;
    const PAGE_IMG = 'https://public.readdy.ai/ai/img_res/e57a9404576e05e9d9686f584bd7ea54.jpg';

    document.title = PAGE_TITLE;

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); const [a, v] = sel.replace('meta[', '').replace(']', '').split('='); el.setAttribute(a.trim(), v.replace(/"/g, '').trim()); document.head.appendChild(el); el.setAttribute('data-page-meta', 'true'); }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'content', PAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:image"]', 'content', PAGE_IMG);
    setMeta('meta[property="og:image:width"]', 'content', '1200');
    setMeta('meta[property="og:image:height"]', 'content', '630');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', PAGE_TITLE);
    setMeta('meta[name="twitter:description"]', 'content', PAGE_DESC);
    setMeta('meta[name="twitter:image"]', 'content', PAGE_IMG);

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          name: 'Pricing Plans – EVENTSHOWCASE',
          description: 'Choose a subscription plan to start creating and promoting events on EVENTSHOWCASE.',
          url: `${SITE_URL}/create`,
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'Can I switch plans later?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, you can upgrade or downgrade at any time. Changes take effect on your next billing cycle.',
              },
            },
            {
              '@type': 'Question',
              name: 'What happens when my paid plan expires?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Your account automatically reverts to the Starter plan. Your events and data are never deleted.',
              },
            },
            {
              '@type': 'Question',
              name: 'Is there a free trial for paid plans?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The Starter plan is free forever and gives you a great way to try the platform. Paid plans start immediately after payment.',
              },
            },
            {
              '@type': 'Question',
              name: 'What payment methods are accepted?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'We accept cards, bank transfers, and mobile money via Paystack and Flutterwave.',
              },
            },
            {
              '@type': 'Question',
              name: 'How does team access work?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Growth plan allows up to 3 team members. Pro Partner allows unlimited members. Invite collaborators by email and assign them Admin, Editor, or Viewer roles.',
              },
            },
          ],
        },
      ],
    };
    const existing = document.getElementById('schema-subscribe');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-subscribe';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { const el = document.getElementById('schema-subscribe'); if (el) el.remove(); document.querySelectorAll('meta[data-page-meta="true"]').forEach(e => e.remove()); };
  }, []);

  useEffect(() => {
    if (!authLoading && user) fetchExistingSubscription();
    else if (!authLoading && !user) setLoadingSub(false);
  }, [authLoading, user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_callback') === 'true') setStep('payment');
  }, []);

  const fetchExistingSubscription = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setExistingSub(data);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    } finally {
      setLoadingSub(false);
    }
  };

  const getMonthlyPrice = (basePrice: number) => {
    if (basePrice === 0) return 0;
    return billingCycle === 'yearly' ? Math.round((basePrice * 10) / 12) : basePrice;
  };

  const getFinalPrice = () => {
    const plan = plans.find(p => p.name === selectedPlan);
    if (!plan) return 0;
    const mp = getMonthlyPrice(plan.price);
    return billingCycle === 'yearly' ? mp * 12 : mp;
  };

  const handleContinue = () => {
    if (!user) { navigate('/login'); return; }
    if (!selectedPlan) return;
    const plan = plans.find(p => p.name === selectedPlan);
    if (!plan) return;
    if (plan.price === 0) { activateFreePlan(); return; }
    setStep('payment');
  };

  const activateFreePlan = async () => {
    if (!user) return;
    const now = new Date();
    const expiresAt = new Date(now);
    expiresAt.setFullYear(expiresAt.getFullYear() + 100);
    await supabase.from('subscriptions').insert({
      user_id: user.id, plan: 'starter', billing_cycle: 'monthly',
      status: 'active', started_at: now.toISOString(), expires_at: expiresAt.toISOString(),
    });
    setSuccessRef('FREE-STARTER');
    setShowSuccess(true);
  };

  const handlePaymentSuccess = (reference: string) => {
    setSuccessRef(reference);
    setShowSuccess(true);
  };

  const activePlan = plans.find(p => p.name === selectedPlan);

  if (authLoading || loadingSub) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {step === 'select' && (
        <>
          {/* Hero */}
          <section className="pt-28 pb-16 px-4 text-center relative overflow-hidden">
            {/* subtle bg grid */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#f0fdf4_1px,transparent_1px),linear-gradient(to_bottom,#f0fdf4_1px,transparent_1px)] bg-[size:48px_48px] opacity-60 pointer-events-none"></div>
            <div className="relative max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-50 border border-teal-200 rounded-full mb-6">
                <i className="ri-vip-crown-line text-teal-600 text-sm"></i>
                <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide">Simple, Transparent Pricing</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                The plan that grows<br />
                <span className="text-teal-600">with your events</span>
              </h1>
              <p className="text-lg text-gray-500 max-w-xl mx-auto mb-8">
                Start free, upgrade when you're ready. No hidden fees — just lower commission as you grow.
              </p>

              {/* Billing toggle */}
              <div className="inline-flex items-center gap-3 bg-gray-100 rounded-full px-2 py-2">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                    billingCycle === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Yearly
                  <span className="text-[10px] font-bold bg-teal-500 text-white px-1.5 py-0.5 rounded-full">-17%</span>
                </button>
              </div>
            </div>
          </section>

          {/* Existing sub banner */}
          {existingSub && (
            <div className="max-w-5xl mx-auto px-4 mb-6">
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-vip-crown-line text-teal-600"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-teal-900">
                    Active <strong className="capitalize">{existingSub.plan.replace(/_/g, ' ')}</strong> subscription
                  </p>
                  <p className="text-xs text-teal-700 mt-0.5">
                    Expires {new Date(existingSub.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <Link to="/dashboard" className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap">
                  Go to Dashboard
                </Link>
              </div>
            </div>
          )}

          {/* Plan cards */}
          <section className="max-w-5xl mx-auto px-4 pb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
              {plans.map(plan => {
                const monthlyPrice = getMonthlyPrice(plan.price);
                const isSelected = selectedPlan === plan.name;

                return (
                  <button
                    key={plan.name}
                    type="button"
                    onClick={() => setSelectedPlan(plan.name)}
                    className={`relative rounded-2xl text-left cursor-pointer transition-all duration-200 flex flex-col w-full ${
                      plan.popular
                        ? 'bg-teal-600 text-white ring-2 ring-teal-600 scale-[1.02]'
                        : isSelected
                        ? 'bg-white ring-2 ring-teal-500'
                        : 'bg-white border border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    {/* Popular ribbon */}
                    {plan.popular && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                        <span className="bg-teal-500 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
                          Most Popular
                        </span>
                      </div>
                    )}

                    {/* Selected check */}
                    {isSelected && !plan.popular && (
                      <div className="absolute top-4 right-4 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                    )}

                    <div className="p-6 flex flex-col flex-1">
                      {/* Badge + name */}
                      <div className="mb-5">
                        <span className={`inline-block text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide mb-3 ${
                          plan.popular ? 'bg-white/20 text-white' : plan.badgeColor
                        }`}>
                          {plan.badge}
                        </span>
                        <h3 className={`text-xl font-bold mb-1 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                          {plan.name}
                        </h3>
                        <p className={`text-xs ${plan.popular ? 'text-teal-100' : 'text-gray-500'}`}>
                          {plan.highlight}
                        </p>
                      </div>

                      {/* Price */}
                      <div className="mb-1">
                        <div className="flex items-baseline gap-1">
                          <span className={`text-sm font-medium ${plan.popular ? 'text-teal-100' : 'text-gray-400'}`}>$</span>
                          <span className={`text-5xl font-extrabold tracking-tight ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                            {monthlyPrice === 0 ? '0' : monthlyPrice}
                          </span>
                          <span className={`text-sm ${plan.popular ? 'text-teal-100' : 'text-gray-400'}`}>/mo</span>
                        </div>
                        {plan.price > 0 && billingCycle === 'yearly' && (
                          <p className={`text-xs mt-1 ${plan.popular ? 'text-teal-100' : 'text-teal-600'}`}>
                            Billed ${monthlyPrice * 12}/yr · 2 months free
                          </p>
                        )}
                        {plan.price > 0 && billingCycle === 'monthly' && (
                          <p className={`text-xs mt-1 ${plan.popular ? 'text-teal-100' : 'text-gray-400'}`}>
                            or ${Math.round((plan.price * 10) / 12)}/mo billed yearly
                          </p>
                        )}
                        {plan.price === 0 && (
                          <p className={`text-xs mt-1 ${plan.popular ? 'text-teal-100' : 'text-gray-400'}`}>No credit card required</p>
                        )}
                      </div>

                      {/* Commission highlight */}
                      <div className={`flex items-center gap-2 mt-4 mb-5 px-3 py-2.5 rounded-xl ${
                        plan.popular ? 'bg-white/15' : 'bg-teal-50'
                      }`}>
                        <i className={`ri-percent-line text-lg ${plan.popular ? 'text-white' : 'text-teal-600'}`}></i>
                        <div>
                          <span className={`text-xl font-extrabold ${plan.popular ? 'text-white' : 'text-teal-700'}`}>{plan.commission}</span>
                          <span className={`text-xs ml-1 ${plan.popular ? 'text-teal-100' : 'text-gray-500'}`}>{plan.commissionLabel}</span>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className={`border-t mb-5 ${plan.popular ? 'border-white/20' : 'border-gray-100'}`}></div>

                      {/* Features */}
                      <ul className="space-y-2.5 flex-1">
                        {plan.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <div className={`w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${
                              f.included
                                ? plan.popular ? 'bg-white/20' : 'bg-teal-100'
                                : 'bg-transparent'
                            }`}>
                              <i className={`text-[10px] ${
                                f.included
                                  ? plan.popular ? 'ri-check-line text-white' : 'ri-check-line text-teal-600'
                                  : 'ri-close-line text-gray-300'
                              }`}></i>
                            </div>
                            <span className={`text-xs leading-snug ${
                              f.included
                                ? plan.popular ? 'text-white' : 'text-gray-700'
                                : 'text-gray-300 line-through'
                            }`}>
                              {f.text}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <div className="mt-6">
                        <div className={`w-full py-3 rounded-xl text-sm font-semibold text-center transition-all ${
                          plan.popular
                            ? 'bg-white text-teal-700 hover:bg-teal-50'
                            : isSelected
                            ? 'bg-teal-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}>
                          {isSelected ? (plan.price === 0 ? 'Selected — Free' : 'Selected') : (plan.price === 0 ? 'Get Started Free' : `Choose ${plan.name}`)}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Continue CTA */}
          <section className="max-w-5xl mx-auto px-4 pb-10 text-center">
            <button
              onClick={handleContinue}
              disabled={!selectedPlan}
              className={`inline-flex items-center gap-2 px-10 py-4 rounded-xl font-semibold text-base transition-all whitespace-nowrap cursor-pointer ${
                selectedPlan
                  ? 'bg-teal-600 hover:bg-teal-700 text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {!selectedPlan
                ? 'Select a Plan to Continue'
                : activePlan && activePlan.price === 0
                ? 'Activate Free Plan'
                : !user
                ? 'Log In to Subscribe'
                : `Continue to Payment — $${getFinalPrice().toFixed(2)}`}
              <i className="ri-arrow-right-line"></i>
            </button>
            {!user && selectedPlan && (
              <p className="text-sm text-gray-500 mt-3">
                Don&apos;t have an account?{' '}
                <Link to="/signup" className="text-teal-600 font-medium hover:underline">Sign up here</Link>
              </p>
            )}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-6 text-xs text-gray-400">
              <span className="flex items-center gap-1.5"><i className="ri-shield-check-line text-teal-500 text-sm"></i> No credit card for free plan</span>
              <span className="flex items-center gap-1.5"><i className="ri-refresh-line text-teal-500 text-sm"></i> Cancel anytime</span>
              <span className="flex items-center gap-1.5"><i className="ri-lock-line text-teal-500 text-sm"></i> Secure payments</span>
            </div>
          </section>

          {/* Commission comparison table */}
          <section className="max-w-5xl mx-auto px-4 pb-16">
            <div className="bg-gray-50 rounded-2xl p-6 sm:p-8">
              <h2 className="text-lg font-bold text-gray-900 mb-1">How much do you actually keep?</h2>
              <p className="text-sm text-gray-500 mb-6">Based on $1,000/month in ticket sales</p>
              <div className="space-y-4">
                {[
                  { plan: 'Starter', rate: 10, sub: 0, color: 'bg-gray-300' },
                  { plan: 'Growth', rate: 5, sub: 19, color: 'bg-teal-500' },
                  { plan: 'Pro Partner', rate: 2, sub: 39, color: 'bg-gray-800' },
                ].map(row => {
                  const commission = 1000 * (row.rate / 100);
                  const net = 1000 - commission - row.sub;
                  const pct = Math.round((net / 1000) * 100);
                  return (
                    <div key={row.plan} className="flex items-center gap-4">
                      <div className="w-24 text-sm font-semibold text-gray-700 flex-shrink-0">{row.plan}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500">You keep ${net}/mo</span>
                          <span className="text-xs font-bold text-gray-700">{pct}%</span>
                        </div>
                        <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${row.color}`} style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-4">* Growth and Pro Partner include subscription cost in the calculation</p>
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-3xl mx-auto px-4 pb-20">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Frequently asked questions</h2>
            <div className="space-y-4">
              {[
                { q: 'Can I switch plans later?', a: 'Yes, you can upgrade or downgrade at any time. Changes take effect on your next billing cycle.' },
                { q: 'What happens when my paid plan expires?', a: 'Your account automatically reverts to the Starter plan. Your events and data are never deleted.' },
                { q: 'Is there a free trial for paid plans?', a: 'The Starter plan is free forever and gives you a great way to try the platform. Paid plans start immediately after payment.' },
                { q: 'What payment methods are accepted?', a: 'We accept cards, bank transfers, and mobile money via Paystack and Flutterwave.' },
                { q: 'How does team access work?', a: 'Growth plan allows up to 3 team members. Pro Partner allows unlimited members. Invite collaborators by email and assign them Admin, Editor, or Viewer roles.' },
              ].map((item, i) => (
                <FaqItem key={i} question={item.q} answer={item.a} />
              ))}
            </div>
          </section>
        </>
      )}

      {step === 'payment' && activePlan && user && (
        <div className="pt-28 pb-20 px-4">
          <div className="max-w-lg mx-auto">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Complete Your Subscription</h2>
              <p className="text-gray-500 text-sm">
                Subscribing to <strong className="text-teal-600">{activePlan.name}</strong> — {billingCycle === 'monthly' ? 'Monthly' : 'Yearly'} billing
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <SubscriptionPayment
                plan={{ name: activePlan.name, price: activePlan.price }}
                billingCycle={billingCycle}
                finalPrice={getFinalPrice()}
                userEmail={user.email || ''}
                userName={profile?.full_name || user.user_metadata?.full_name || ''}
                userId={user.id}
                onSuccess={handlePaymentSuccess}
                onBack={() => setStep('select')}
              />
            </div>
          </div>
        </div>
      )}

      <Footer />

      {showSuccess && activePlan && (
        <SubscriptionSuccess
          planName={activePlan.name}
          billingCycle={billingCycle}
          reference={successRef}
          onClose={() => setShowSuccess(false)}
        />
      )}
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-900">{question}</span>
        <i className={`ri-arrow-down-s-line text-gray-400 text-lg transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>
      {open && (
        <div className="px-5 pb-4">
          <p className="text-sm text-gray-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}
